/**
 * @fileoverview Logout mutation hook.
 * @description Notifies the identity backend to revoke the current session, then clears the local
 * session and sends the visitor to /login regardless of whether that request succeeds — a signed-
 * out UI should never be blocked on a network call. Lives in shared (not features/auth) because its
 * only consumer, DashboardLayout, is itself a shared component (per ADR-0002, shared can't import
 * from a feature).
 *
 * Also clears the entire TanStack Query cache. Every cached query (GET /identity/me, notification
 * history, sales/banking data, ...) is scoped to whichever account was signed in when it was
 * fetched; without this, its stale queryKey-cache would still be sitting there — and, since this
 * app's default `staleTime` is 0 but `gcTime` isn't, TanStack Query serves that stale cached value
 * synchronously to the next signed-in user's very first render (while a background refetch is
 * still in flight) rather than waiting for a fresh one. RequirePrivilege reads
 * `useCurrentSession()`'s privileges to decide route access on exactly that first render, so a
 * user signing in right after a different account signed out could 403 on their own home route:
 * checked against the previous account's leftover privileges for the instant before the refetch
 * resolves.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { httpClient } from '@/shared/api/http-client'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useAuthStore } from '@/shared/lib/auth/auth.store'

interface LogoutResponse {
  loggedOut: boolean
}

/**
 * @description Revokes the caller's current session server-side. The response is never read — see
 * the file header for why local state is cleared unconditionally regardless of the outcome.
 * @returns {Promise<LogoutResponse>} Whether the session was logged out.
 */
async function logoutRequest(): Promise<LogoutResponse> {
  const { data } = await httpClient.post<LogoutResponse>('/identity/logout')
  return data
}

/**
 * @description Signs the current user out: revokes the session server-side (best-effort), then
 * always clears local state and the query cache, and navigates to /login.
 * @returns {ReturnType<typeof useMutation<void, unknown, void>>} The mutation object (mutate,
 * isPending, ...).
 */
export function useLogout() {
  const logout = useAuthStore((state) => state.logout)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      logout()
      queryClient.clear()
      void navigate(ROUTES.LOGIN, { replace: true })
    },
  })
}
